import { DEVICE_KEY, config, installId, state } from './state';
import type { MetricEvent } from './types';

export function deviceToken(): string {
  return localStorage.getItem(DEVICE_KEY) ?? '';
}

export function setDeviceToken(token: string): void {
  localStorage.setItem(DEVICE_KEY, token);
}

function headers(): Record<string, string> {
  const out: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = deviceToken();
  if (token) out['X-Talkxi-Device'] = token;
  return out;
}

export async function deviceFetch(
  path: string, options: RequestInit = {}, timeoutMs = 60_000,
): Promise<Response> {
  return fetch(path, {
    ...options,
    signal: options.signal ?? AbortSignal.timeout(timeoutMs),
    headers: { ...(options.headers as Record<string, string> ?? {}), ...headers() },
  });
}

export async function postJson<T>(path: string, body: unknown, timeoutMs?: number): Promise<{ ok: boolean; data: T }> {
  const res = await deviceFetch(path, { method: 'POST', body: JSON.stringify(body) }, timeoutMs);
  const data = (await res.json().catch(() => ({}))) as T;
  return { ok: res.ok, data };
}

/** Mensaje de error legible: distingue "tablet sin autorizar" del resto. */
export function apiMessage(data: { error?: string; message?: string } | null, fallback: string, authText: string): string {
  if (data?.error === 'device_not_authorized') return authText;
  return data?.message ?? fallback;
}

export async function metric(event: MetricEvent): Promise<void> {
  if (!deviceToken()) return;
  try {
    await postJson('/api/metrics.php', { action: 'record', event, lang: state.lang }, 8_000);
  } catch { /* las metricas nunca deben romper la experiencia del pasajero */ }
}

export async function heartbeat(): Promise<void> {
  if (!navigator.onLine || !deviceToken() || document.visibilityState === 'hidden') return;
  try {
    await postJson('/api/heartbeat.php', {}, 8_000);
  } catch { /* silencioso */ }
}

interface BootstrapResponse {
  brand_name?: string; tenant_id?: string; tenant_name?: string; vehicle_id?: string;
  deployment_id?: string; public_domain?: string; app_url?: string; website?: string;
  privacy_url?: string; whatsapp?: string; google_review_url?: string; google_review_qr?: string;
  service_area?: string; default_route_from?: string; default_route_to?: string;
  max_passengers?: number; features?: Record<string, boolean>;
}

export async function loadPublicConfig(): Promise<void> {
  try {
    const res = await deviceFetch('/api/bootstrap.php', { method: 'GET', cache: 'no-store' }, 10_000);
    if (!res.ok) return;
    const d = (await res.json()) as BootstrapResponse;

    const assign = <K extends keyof typeof config>(key: K, value: (typeof config)[K] | undefined) => {
      if (value !== undefined) config[key] = value;
    };
    assign('brandName', d.brand_name);
    assign('tenantId', d.tenant_id);
    assign('tenantName', d.tenant_name);
    assign('vehicleId', d.vehicle_id);
    assign('deploymentId', d.deployment_id);
    assign('publicDomain', d.public_domain);
    assign('appUrl', d.app_url);
    assign('website', d.website);
    assign('privacyUrl', d.privacy_url);
    assign('whatsapp', d.whatsapp);
    assign('googleReviewUrl', d.google_review_url);
    assign('googleReviewQr', d.google_review_qr);
    assign('serviceArea', d.service_area);
    assign('defaultRouteFrom', d.default_route_from);
    assign('defaultRouteTo', d.default_route_to);
    if (d.max_passengers !== undefined) config.maxPassengers = Number(d.max_passengers) || 6;
    assign('features', d.features);

    // Si la tablet cambia de empresa o de taxi, la ruta guardada deja de valer.
    const scope = `${config.tenantId}|${config.vehicleId}`;
    if (localStorage.getItem('talkxiRouteScope') !== scope) {
      localStorage.removeItem('t2aroute');
      state.route = { key: 'configured', from: '', to: '' };
      localStorage.setItem('talkxiRouteScope', scope);
    }
    document.title = `${config.brandName} — AI Passenger Concierge`;
  } catch { /* se sigue con la config por defecto */ }
}

export interface DeviceCheck {
  authorized: boolean;
  scope?: { tenant_id?: string; vehicle_id?: string; deployment_id?: string; install_id?: string };
}

export async function checkDevice(): Promise<DeviceCheck> {
  const { data } = await postJson<DeviceCheck>('/api/auth.php', { action: 'check', install_id: installId() }, 10_000);
  return data;
}

export async function enrollTablet(enrollmentCode: string): Promise<DeviceCheck & { device_token?: string }> {
  const res = await fetch('/api/auth.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'authorize', enrollment_code: enrollmentCode, install_id: installId() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.authorized || !data.device_token) {
    throw new Error(data.message ?? 'No se pudo autorizar la tablet');
  }
  setDeviceToken(data.device_token);
  return data;
}

export async function unlockDriver(pin: string): Promise<void> {
  const { ok, data } = await postJson<{ driver_unlocked?: boolean; message?: string }>(
    '/api/auth.php', { action: 'driver_unlock', pin }, 10_000,
  );
  if (!ok || !data.driver_unlocked) throw new Error(data.message ?? 'PIN de conductor incorrecto');
}
